export const translations = {
  en: {
    nav: {
      home: 'Home',
      steps: 'Steps',
      differentiation: 'Niche Finder',
      contact: 'Contact',
      login: 'Login',
      logout: 'Logout',
      getStarted: 'Start for free'
    },
    hero: {
      subtitle: 'The 10-step methodology to transform your idea into a market-ready product.',
      subtitleHighlight: 'Structured. Proven. Effective.',
      ctaPrimary: 'Start for free',
      ctaSecondary: 'Discover More',
      feature1: 'Structured Process',
      feature1Desc: 'Follow a proven 10-step plan from vision to launch without losing focus.',
      feature2: 'Data-Driven Decisions',
      feature2Desc: 'Validate your ideas with real market feedback before building the product.',
      feature3: 'Expert Mentoring',
      feature3Desc: 'Get 1:1 guidance from experienced founders who have been there.',
      usp: {
        badge: 'Co-Creation & Mentoring',
        title: 'Your fast track to a successful launch',
        column1: {
          badge: 'Mentoring',
          title: 'Consulting Sessions',
          subtitle: 'Expert Guidance',
          desc: 'Get personal 1:1 mentoring to refine your strategy, overcome roadblocks, and accelerate your progress with actionable feedback.'
        },
        column2: {
          badge: 'Build',
          title: 'Co-Creation Build',
          subtitle: 'Technical Execution',
          desc: 'We build your MVP together. Watch your idea turn into a production-ready application in live collaborative sessions.'
        }
      }
    },
    problem: {
      title: 'Lost in the Founder Chaos?',
      subtitle: 'Most founders fail not because of bad ideas, but because they lose focus in the execution phase.',
      chaosLabel: 'The Founder Chaos',
      clarityLabel: 'The Clear Path',
      overwhelmedTitle: 'Overwhelmed by decisions?',
      overwhelmedDesc: 'Too many marketing channels, conflicting advice, and endless feature ideas lead to analysis paralysis.',
      solutionTitle: 'Focus on what matters',
      solutionDesc: 'Our methodology forces you to focus on the 20% of actions that drive 80% of the results.',
      testimonialsTitle: 'From Chaos to Clarity',
      testimonialsSubtitle: 'See how others have transformed their chaotic ideas into structured, growing businesses.',
      methodologySubtitle: 'The 10 Steps to Your Success',
      testimonials: [
        {
          before: "I had 100 ideas but didn't know where to start. I was completely paralyzed.",
          after: 'The structured steps gave me exactly the focus I needed to launch within 6 weeks.',
          name: 'Sarah M.',
          role: 'SaaS Founder'
        },
        {
          before: 'We built a product for 6 months without ever talking to a customer.',
          after: 'Step 2 forced us to validate. We pivoted and got our first paying customers in days.',
          name: 'Thomas K.',
          role: 'E-commerce'
        },
        {
          before: 'Marketing felt like gambling. We wasted thousands on ads with zero ROI.',
          after: 'The strategy canvas helped us nail our messaging. CAC dropped by 70%.',
          name: 'Julia R.',
          role: 'Agency Owner'
        }
      ]
    },
    overview: {
      title: 'The Proven 10-Step Framework',
      subtitle: 'Follow our structured methodology to turn your idea into a successful business.',
      step: 'Step',
      learnMore: 'Learn More'
    },
    differentiation: {
      saved: 'Saved successfully!',
      title: 'Blue Ocean Strategy',
      subtitle: 'Find your uncontested market space and make the competition irrelevant.',
      process1: 'Current State',
      process1Desc: 'Map the current industry standards',
      process2: 'Analyze',
      process2Desc: 'Identify pain points and over-served areas',
      process3: 'ERRC Grid',
      process3Desc: 'Eliminate, Reduce, Raise, Create',
      process4: 'New Curve',
      process4Desc: 'Plot your new value curve',
      loginRequired: 'Login Required',
      errcTitle: 'The Four Actions Framework',
      errcSubtitle: 'Reconstruct buyer value elements to break the value-cost trade-off.',
      eliminate: 'Eliminate',
      eliminateDesc: 'Which factors that the industry takes for granted should be eliminated?',
      reduce: 'Reduce',
      reduceDesc: 'Which factors should be reduced well below the industry standard?',
      raise: 'Raise',
      raiseDesc: 'Which factors should be raised well above the industry standard?',
      create: 'Create',
      createDesc: 'Which factors should be created that the industry has never offered?'
    },
    contact: {
      title: 'Get in Touch',
      subtitle: 'Have questions about our methodology or need custom mentoring? We are here to help.',
      name: 'Your Name',
      email: 'Email Address',
      subject: 'Subject',
      message: 'Message',
      gdpr: 'I agree to the processing of my data according to the privacy policy.',
      submit: 'Send Message',
      success: 'Message sent successfully!',
      infoTitle: 'Contact Information',
      emailLabel: 'Email us',
      workingHours: 'Mon-Fri, 9am - 6pm CET',
      locationLabel: 'Visit us'
    },
    footer: {
      desc: 'Transforming ideas into successful businesses through structured methodology and expert guidance.',
      quickLinks: 'Quick Links',
      contact: 'Contact',
      rights: '© 2024 Stup-On. All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      cookies: 'Cookie Settings'
    },
    auth: {
      title: 'Create Account',
      subtitle: 'Join today and build your success story.',
      email: 'Email Address',
      password: 'Password',
      submit: 'Start for free',
      alreadyHaveAccount: 'Already have an account? Sign In',
      noAccount: "Don't have an account? Sign Up",
      loginTitle: 'Welcome Back',
      loginSubtitle: 'Sign in to continue your journey.',
      userExistsError: 'An account with this email already exists. Please log in instead.'
    },
    detail: {
      back: 'Back to Overview',
      stepOf: 'Step {current} of 10',
      overview: 'Overview',
      actionItems: 'Your Action Items',
      keyFocus: 'Key Focus Points',
      continueTo: 'Continue to Step',
      step1ToolTitle: 'Your Vision Tool',
      step1Question1: 'What specific problem does your idea solve?',
      step1Question2: 'For whom (target group) do you solve this?',
      step1Question3: 'Why are you suitable to solve this?',
      step1Question4: 'What is the product name and slogan?',
      step1Placeholder: 'Type your thoughts here...',
      step1Save: 'Save Vision',
      step1Saving: 'Saving...',
      step1Saved: 'Saved Successfully!',
      step3ToolTitle: 'Your Idea Tool',
      step3Question1: 'What specific problem does your idea solve?',
      step3Question2: 'For whom (target group) do you solve this?',
      step3Question3: 'Why are you suitable to solve this?',
      step3Question4: 'What is the product name and slogan?',
      step3Placeholder: 'Type your thoughts here...',
      step3Save: 'Save Idea',
      step3Saving: 'Saving...',
      step3Saved: 'Saved Successfully!',
      lockedTitle: 'Content Locked',
      lockedDesc: 'Unlock premium to access the full potential of this step.',
      unlockNow: 'Unlock Now',
      alreadyPurchased: 'Already purchased?',
      authPrompt: 'Please log in or register with the email address used for your purchase to access your premium content.',
      login: 'Log In',
      register: 'Register',
      step5Low: 'Low',
      step5Medium: 'Medium',
      step5High: 'High',
      step5UseCase: 'Use Case / Target Audience',
      step5Placeholder: 'Describe the specific use case or target audience for this strategy...',
      step5Elements: 'Value Factors',
      step5Curve1: 'Your Offering',
      step5Curve2: 'Industry Standard',
      step5AddElement: 'Add Value Factor',
      step5Save: 'Save Strategy',
      step5ChartTitle: 'Strategy Canvas'
    },
    paths: {
      home: '/',
      steps: '/steps',
      differentiation: '/find-your-niche',
      contact: '/contact',
      login: '/login',
      register: '/register',
      privacy: '/privacy',
      terms: '/terms',
      cookies: '/cookies'
    }
  },
  de: {
    nav: {
      home: 'Start',
      steps: 'Schritte',
      differentiation: 'Nischen-Finder',
      contact: 'Kontakt',
      login: 'Anmelden',
      logout: 'Abmelden',
      getStarted: 'Kostenlos starten'
    },
    hero: {
      subtitle: 'Die 10-Schritte-Methodik, um deine Idee in ein marktreifes Produkt zu verwandeln.',
      subtitleHighlight: 'Mit der Stup-On Methode bleibst du am Ball und baust ganz nebenbei dein eigenes Business auf 🚀.',
      ctaPrimary: 'Kostenlos starten',
      ctaSecondary: 'Mehr entdecken',
      feature1: 'Strukturierter Prozess',
      feature1Desc: 'Folge einem bewährten 10-Schritte-Plan von der Vision bis zum Launch, ohne dabei den Fokus zu verlieren.',
      feature2: 'Triff die richtigen Entscheidungen',
      feature2Desc: 'Validiere deine Ideen, bevor du das Produkt baust.',
      feature3: 'Experten-Mentoring',
      feature3Desc: 'Erhalte 1:1 Betreuung von erfahrenen Gründern, die diesen Weg bereits gegangen sind.',
      usp: {
        badge: 'Co-Creation & Mentoring',
        title: 'Dein Überholweg zum erfolgreichen Launch',
        column1: {
          badge: 'Mentoring',
          title: 'Consulting Sessions',
          subtitle: 'Experten-Betreuung',
          desc: 'Erhalte dein persönliches 1:1 Mentoring, um deine Strategie zu schärfen, Hindernisse zu überwinden und deinen Fortschritt mit umsetzbarem Feedback zu beschleunigen.'
        },
        column2: {
          badge: 'Build',
          title: 'Co-Creation Build',
          subtitle: 'Technische Umsetzung',
          desc: 'Wir bauen dein MVP gemeinsam. Erlebe, wie deine Idee in gemeinsamen Live-Sessions zu einer produktionsreifen Anwendung wird.'
        }
      }
    },
    problem: {
      title: 'Verloren im Gründer-Chaos?',
      subtitle: 'Die meisten Gründer scheitern nicht an schlechten Ideen, sondern weil sie in der Umsetzungsphase den Fokus verlieren.',
      chaosLabel: 'Das Gründer-Chaos',
      clarityLabel: 'Der klare Weg',
      overwhelmedTitle: 'Überwältigt von Entscheidungen?',
      overwhelmedDesc: 'Zu viele Marketingkanäle, widersprüchliche Ratschläge und endlose Feature-Ideen führen zur Überforderung.',
      solutionTitle: 'Fokus auf das Wesentliche',
      solutionDesc: 'Unsere Methodik zwingt dich, dich auf die 20% der Aktionen zu konzentrieren, die 80% der Ergebnisse bringen.',
      testimonialsTitle: 'Vom Chaos zur Klarheit',
      testimonialsSubtitle: 'Sieh, wie andere ihre chaotischen Ideen in strukturierte, wachsende Unternehmen verwandelt haben.',
      methodologySubtitle: 'Die 10 Schritte zu deinem Erfolg',
      testimonials: [
        {
          before: 'Ich hatte 100 Ideen, wusste aber nicht, wo ich anfangen sollte. Ich war komplett blockiert.',
          after: 'Die strukturierten Schritte gaben mir genau den Fokus, den ich brauchte, um in 6 Wochen zu launchen.',
          name: 'Sarah M.',
          role: 'SaaS-Gründerin'
        },
        {
          before: 'Wir haben 6 Monate lang ein Produkt gebaut, ohne je mit einem Kunden zu sprechen.',
          after: 'Schritt 2 zwang uns zur Validierung. Wir haben den Kurs geändert und in Tagen zahlende Kunden gewonnen.',
          name: 'Thomas K.',
          role: 'E-Commerce'
        },
        {
          before: 'Marketing fühlte sich wie Glücksspiel an. Wir haben Tausende für Ads ohne ROI verschwendet.',
          after: 'Das Strategie-Canvas half uns, unsere Botschaft auf den Punkt zu bringen. CAC sank um 70%.',
          name: 'Julia R.',
          role: 'Agentur-Inhaberin'
        }
      ]
    },
    overview: {
      title: 'Das bewährte 10-Schritte-System',
      subtitle: 'Folge unserer strukturierten Methodik, um deine Idee in ein erfolgreiches Unternehmen zu verwandeln.',
      step: 'Schritt',
      learnMore: 'Mehr erfahren'
    },
    differentiation: {
      saved: 'Erfolgreich gespeichert!',
      title: 'Blue Ocean Strategie',
      subtitle: 'Finde deinen unangetasteten Markt und mache die Konkurrenz irrelevant.',
      process1: 'Ist-Zustand',
      process1Desc: 'Erfasse die aktuellen Branchenstandards',
      process2: 'Analysieren',
      process2Desc: 'Identifiziere Schmerzpunkte und überversorgte Bereiche',
      process3: 'ERRC-Raster',
      process3Desc: 'Eliminieren, Reduzieren, Steigern, Kreieren',
      process4: 'Neue Kurve',
      process4Desc: 'Zeichne deine neue Nutzenkurve',
      loginRequired: 'Anmeldung erforderlich',
      errcTitle: 'Das Vier-Aktionen-Format',
      errcSubtitle: 'Rekonstruiere die Nutzen-Elemente, um den Trade-off zwischen Wert und Kosten zu durchbrechen.',
      eliminate: 'Eliminieren',
      eliminateDesc: 'Welche Faktoren, die die Branche als selbstverständlich ansieht, sollten eliminiert werden?',
      reduce: 'Reduzieren',
      reduceDesc: 'Welche Faktoren sollten weit unter den Branchenstandard reduziert werden?',
      raise: 'Steigern',
      raiseDesc: 'Welche Faktoren sollten weit über den Branchenstandard gesteigert werden?',
      create: 'Kreieren',
      createDesc: 'Welche Faktoren sollten kreiert werden, die die Branche noch nie angeboten hat?'
    },
    contact: {
      title: 'Kontakt aufnehmen',
      subtitle: 'Hast du Fragen zu unserer Methodik oder benötigst du individuelles Mentoring? Wir sind hier, um zu helfen.',
      name: 'Dein Name',
      email: 'E-Mail-Adresse',
      subject: 'Betreff',
      message: 'Nachricht',
      gdpr: 'Ich stimme der Verarbeitung meiner Daten gemäß der Datenschutzerklärung zu.',
      submit: 'Nachricht senden',
      success: 'Nachricht erfolgreich gesendet!',
      infoTitle: 'Kontaktinformationen',
      emailLabel: 'Schreib uns',
      workingHours: 'Mo-Fr, 09:00 - 18:00 MEZ',
      locationLabel: 'Besuch uns'
    },
    footer: {
      desc: 'Wir verwandeln Ideen durch strukturierte Methodik und Expertenbegleitung in erfolgreiche Unternehmen.',
      quickLinks: 'Schnelllinks',
      contact: 'Kontakt',
      rights: '© 2024 Stup-On. All rights reserved.',
      privacy: 'Datenschutz',
      terms: 'AGB',
      cookies: 'Cookie-Einstellungen'
    },
    auth: {
      title: 'Konto erstellen',
      subtitle: 'Werde Teil der Erfolgsspur und baue dein Business auf.',
      email: 'E-Mail-Adresse',
      password: 'Passwort',
      submit: 'Kostenlos starten',
      alreadyHaveAccount: 'Bereits ein Konto? Anmelden',
      noAccount: 'Noch kein Konto? Registrieren',
      loginTitle: 'Willkommen zurück',
      loginSubtitle: 'Melde dich an, um fortzufahren.',
      userExistsError: 'Ein Konto mit dieser E-Mail existiert bereits. Bitte melde dich stattdessen an.'
    },
    detail: {
      back: 'Zurück zur Übersicht',
      stepOf: 'Schritt {current} von 10',
      overview: 'Überblick',
      actionItems: 'Deine Aufgaben',
      keyFocus: 'Fokus-Punkte',
      continueTo: 'Weiter zu Schritt',
      step1ToolTitle: 'Dein Visions-Tool',
      step1Question1: 'Welches spezifische Problem löst deine Idee?',
      step1Question2: 'Für wen (Zielgruppe) löst du dieses Problem?',
      step1Question3: 'Warum bist du geeignet, dieses Problem zu lösen?',
      step1Question4: 'Wie soll dein Produkt heißen und welcher Slogan wird genutzt?',
      step1Placeholder: 'Schreibe hier deine Gedanken...',
      step1Save: 'Vision speichern',
      step1Saving: 'Speichert...',
      step1Saved: 'Erfolgreich gespeichert!',
      step3ToolTitle: 'Dein Ideen-Tool',
      step3Question1: 'Welches spezifische Problem löst deine Idee?',
      step3Question2: 'Für wen (Zielgruppe) löst du dieses Problem?',
      step3Question3: 'Warum bist du geeignet, dieses Problem zu lösen?',
      step3Question4: 'Wie soll dein Produkt heißen und welcher Slogan wird genutzt?',
      step3Placeholder: 'Schreibe hier deine Gedanken...',
      step3Save: 'Idee speichern',
      step3Saving: 'Speichert...',
      step3Saved: 'Erfolgreich gespeichert!',
      lockedTitle: 'Inhalt gesperrt',
      lockedDesc: 'Schalte Premium frei, um das volle Potenzial dieses Schrittes zu nutzen.',
      unlockNow: 'Jetzt freischalten',
      alreadyPurchased: 'Bereits gekauft?',
      authPrompt: 'Bitte melde dich an oder registriere dich mit der E-Mail-Adresse, die du für den Kauf verwendet hast, um auf deine Premium-Inhalte zuzugreifen.',
      login: 'Anmelden',
      register: 'Registrieren',
      step5Low: 'Niedrig',
      step5Medium: 'Mittel',
      step5High: 'Hoch',
      step5UseCase: 'Anwendungsfall / Zielgruppe',
      step5Placeholder: 'Beschreibe den spezifischen Anwendungsfall oder die Zielgruppe für diese Strategie...',
      step5Elements: 'Wertfaktoren',
      step5Curve1: 'Dein Angebot',
      step5Curve2: 'Branchenstandard',
      step5AddElement: 'Wertfaktor hinzufügen',
      step5Save: 'Strategie speichern',
      step5ChartTitle: 'Strategie-Canvas'
    },
    paths: {
      home: '/',
      steps: '/schritte',
      differentiation: '/finde-deine-Nische',
      contact: '/kontakt',
      login: '/anmelden',
      register: '/registrieren',
      privacy: '/datenschutz',
      terms: '/agb',
      cookies: '/cookies'
    }
  }
};

export const stepsTranslations = {
  en: [
    {
      id: 1,
      title: "The Vision",
      quote: "Vision is the art of seeing things invisible.",
      quoteAuthor: "Jonathan Swift",
      description: "Define the core problem you want to solve and the future you want to create.",
      subtitle: "Define your core problem and create a compelling vision for the future.",
      content: "Your vision is your North Star. It guides every decision you make and inspires others to join your cause. In this step, we'll drill down into the 'Why' behind your business idea.",
      keyPoints: ["Problem Identification", "Target Audience", "Founder Suitability", "Product Identity"],
      actionItems: [
        "Clearly articulate the #1 problem you solve",
        "Define your ideal customer persona",
        "List 3 reasons why you are the best person for this",
        "Draft 5 potential product names and slogans"
      ]
    },
    {
      id: 2,
      title: "Market Analysis",
      quote: "The goal of a designer is to listen, observe, understand, sympathize, empathize, synthesize, and glean insights.",
      quoteAuthor: "Hillman Curtis",
      description: "Understand the competitive landscape and identify your unique opportunity.",
      subtitle: "Understand the competitive landscape and find your unique opportunity.",
      content: "Don't build in a vacuum. By analyzing what already exists, you can find the gaps and opportunities that others have missed.",
      keyPoints: ["Competitor Mapping", "Market Trends", "Gap Identification", "SWOT Analysis"],
      actionItems: [
        "List 5 direct and indirect competitors",
        "Analyze competitor pricing and features",
        "Identify 3 current trends in your industry",
        "Write down your unique selling proposition (USP)"
      ]
    },
    {
      id: 3,
      title: "Your Ideas",
      quote: "The best way to have a good idea is to have a lot of ideas.",
      quoteAuthor: "Linus Pauling",
      description: "Brainstorm and evaluate potential solutions to the problem you identified.",
      subtitle: "Brainstorm and evaluate potential solutions to your identified problem.",
      content: "Now it's time to generate as many ideas as possible. Don't censor yourself yet. The goal is quantity, which we will later filter for quality.",
      keyPoints: ["Brainstorming Techniques", "Idea Filtering", "Feasibility Check", "Impact vs. Effort"],
      actionItems: [
        "Generate 20 different ways to solve the problem",
        "Group similar ideas into 3-5 core concepts",
        "Rank concepts based on your skills and resources",
        "Select the top idea to move forward with"
      ]
    },
    {
      id: 4,
      title: "The Offering",
      description: "Detailed description of step 4...",
      subtitle: "Shape your core offering and define what makes it irresistible.",
      content: "Full content for step 4...",
      keyPoints: ["Value Proposition", "Feature Set", "Pricing Strategy", "Packaging"],
      actionItems: ["Item 1", "Item 2"]
    },
    {
      id: 5,
      title: "Strategy Canvas",
      description: "Detailed description of step 5...",
      subtitle: "Visualize your strategy and break away from the competition.",
      content: "Full content for step 5...",
      keyPoints: ["Value Curves", "ERRC Grid", "Differentiation", "Focus"],
      actionItems: ["Item 1", "Item 2"]
    },
    {
      id: 6,
      title: "Business Model",
      description: "Detailed description of step 6...",
      subtitle: "Design a sustainable and scalable business model.",
      content: "Full content for step 6...",
      keyPoints: ["Revenue Streams", "Cost Structure", "Key Partnerships", "Channels"],
      actionItems: ["Item 1", "Item 2"]
    },
    {
      id: 7,
      title: "Marketing Strategy",
      description: "Detailed description of step 7...",
      subtitle: "Create a plan to reach your audience and drive growth.",
      content: "Full content for step 7...",
      keyPoints: ["Acquisition Channels", "Content Strategy", "Brand Voice", "Conversion Funnel"],
      actionItems: ["Item 1", "Item 2"]
    },
    {
      id: 8,
      title: "Operations",
      description: "Detailed description of step 8...",
      subtitle: "Set up the processes and systems needed to run efficiently.",
      content: "Full content for step 8...",
      keyPoints: ["Tools & Tech Stack", "Team Building", "Legal & Compliance", "Workflow"],
      actionItems: ["Item 1", "Item 2"]
    },
    {
      id: 9,
      title: "Financials",
      description: "Detailed description of step 9...",
      subtitle: "Plan your budget, funding needs, and financial projections.",
      content: "Full content for step 9...",
      keyPoints: ["Budgeting", "Cash Flow", "Unit Economics", "Funding Strategy"],
      actionItems: ["Item 1", "Item 2"]
    },
    {
      id: 10,
      title: "Launch Plan",
      description: "Detailed description of step 10...",
      subtitle: "Prepare everything for a successful go-to-market execution.",
      content: "Full content for step 10...",
      keyPoints: ["Pre-launch Checklist", "Launch Event", "Post-launch Support", "Iteration"],
      actionItems: ["Item 1", "Item 2"]
    }
  ],
  de: [
    {
      id: 1,
      title: "Die Vision",
      quote: "Vision ist die Kunst, Unsichtbares zu sehen.",
      quoteAuthor: "Jonathan Swift",
      description: "Definiere das Kernproblem, das du lösen willst, und die Zukunft, die du erschaffen möchtest.",
      subtitle: "Definiere das Kernproblem und erschaffe eine überzeugende Zukunftsvision.",
      content: "Deine Vision ist dein Nordstern. Sie leitet jede Entscheidung, die du triffst, und inspiriert andere, sich deiner Sache anzuschließen. In diesem Schritt gehen wir dem 'Warum' hinter deiner Geschäftsidee auf den Grund.",
      keyPoints: ["Problem-Identifikation", "Zielgruppen-Analyse", "Gründer-Eignung", "Produkt-Identität"],
      actionItems: [
        "Formuliere klar das #1 Problem, das du löst",
        "Definiere dein ideales Kundenprofil",
        "Nenne 3 Gründe, warum du die beste Person dafür bist",
        "Entwirf 5 potenzielle Produktnamen und Slogans"
      ]
    },
    {
      id: 2,
      title: "Marktanalyse",
      quote: "Das Ziel eines Designers ist es, zuzuhören, zu beobachten, zu verstehen, mitzufühlen, zu synthetisieren und Erkenntnisse zu gewinnen.",
      quoteAuthor: "Hillman Curtis",
      description: "Verstehe die Wettbewerbslandschaft und identifiziere deine einzigartige Chance.",
      subtitle: "Verstehe den Wettbewerb und finde deine einzigartige Chance.",
      content: "Baue nicht im luftleeren Raum. Indem du analysierst, was bereits existiert, kannst du die Lücken und Chancen finden, die andere übersehen haben.",
      keyPoints: ["Wettbewerbs-Mapping", "Markttrends", "Lücken-Identifikation", "SWOT-Analyse"],
      actionItems: [
        "Liste 5 direkte und indirekte Wettbewerber auf",
        "Analysiere Preise und Funktionen der Konkurrenz",
        "Identifiziere 3 aktuelle Trends in deiner Branche",
        "Schreibe dein Alleinstellungsmerkmal (USP) auf"
      ]
    },
    {
      id: 3,
      title: "Deine Ideen",
      quote: "Der beste Weg, eine gute Idee zu haben, ist, viele Ideen zu haben.",
      quoteAuthor: "Linus Pauling",
      description: "Brainstorme und bewerte potenzielle Lösungen für das von dir identifizierte Problem.",
      subtitle: "Sammle und bewerte Ideen zur Lösung deines Kernproblems.",
      content: "Jetzt ist es an der Zeit, so viele Ideen wie möglich zu generieren. Zensiere dich noch nicht selbst. Das Ziel ist Quantität, die wir später nach Qualität filtern werden.",
      keyPoints: ["Brainstorming-Techniken", "Ideen-Filterung", "Machbarkeitsprüfung", "Impact vs. Aufwand"],
      actionItems: [
        "Generiere 20 verschiedene Wege, um das Problem zu lösen",
        "Gruppiere ähnliche Ideen in 3-5 Kernkonzepte",
        "Bewerte Konzepte basierend auf deinen Fähigkeiten und Ressourcen",
        "Wähle die Top-Idee aus, um fortzufahren"
      ]
    },
    {
      id: 4,
      title: "Das Angebot",
      description: "Detaillierte Beschreibung von Schritt 4...",
      subtitle: "Forme dein Kernangebot und deine Unwiderstehlichkeit.",
      content: "Vollständiger Inhalt für Schritt 4...",
      keyPoints: ["Wertversprechen", "Funktionsumfang", "Preisstrategie", "Paketierung"],
      actionItems: ["Punkt 1", "Punkt 2"]
    },
    {
      id: 5,
      title: "Strategie-Canvas",
      description: "Detaillierte Beschreibung von Schritt 5...",
      subtitle: "Visualisiere deine Strategie und setze dich vom Wettbewerb ab.",
      content: "Vollständiger Inhalt für Schritt 5...",
      keyPoints: ["Werte-Kurven", "ERRC-Raster", "Differenzierung", "Fokus"],
      actionItems: ["Punkt 1", "Punkt 2"]
    },
    {
      id: 6,
      title: "Geschäftsmodell",
      description: "Detaillierte Beschreibung von Schritt 6...",
      subtitle: "Entwerfe ein nachhaltiges und skalierbares Geschäftsmodell.",
      content: "Vollständiger Inhalt für Schritt 6...",
      keyPoints: ["Einnahmequellen", "Kostenstruktur", "Schlüsselpartnerschaften", "Kanäle"],
      actionItems: ["Punkt 1", "Punkt 2"]
    },
    {
      id: 7,
      title: "Marketing-Strategie",
      description: "Detaillierte Beschreibung von Schritt 7...",
      subtitle: "Erstelle einen Plan, um deine Zielgruppe zu erreichen.",
      content: "Vollständiger Inhalt für Schritt 7...",
      keyPoints: ["Akquise-Kanäle", "Content-Strategie", "Markenstimme", "Conversion-Funnel"],
      actionItems: ["Punkt 1", "Punkt 2"]
    },
    {
      id: 8,
      title: "Operationen",
      description: "Detaillierte Beschreibung von Schritt 8...",
      subtitle: "Richte die Prozesse für einen reibungslosen Ablauf ein.",
      content: "Vollständiger Inhalt für Schritt 8...",
      keyPoints: ["Tools & Tech-Stack", "Teamaufbau", "Recht & Compliance", "Workflows"],
      actionItems: ["Punkt 1", "Punkt 2"]
    },
    {
      id: 9,
      title: "Finanzen",
      description: "Detaillierte Beschreibung von Schritt 9...",
      subtitle: "Plane dein Budget, Finanzbedarf und Prognosen.",
      content: "Vollständiger Inhalt für Schritt 9...",
      keyPoints: ["Budgetierung", "Cashflow", "Unit Economics", "Finanzierungsstrategie"],
      actionItems: ["Punkt 1", "Punkt 2"]
    },
    {
      id: 10,
      title: "Launch-Plan",
      description: "Detaillierte Beschreibung von Schritt 10...",
      subtitle: "Bereite alles für eine erfolgreiche Markteinführung vor.",
      content: "Vollständiger Inhalt für Schritt 10...",
      keyPoints: ["Pre-Launch-Checkliste", "Launch-Event", "Post-Launch-Support", "Iterationen"],
      actionItems: ["Punkt 1", "Punkt 2"]
    }
  ]
};